import React, { useState } from 'react';
import VerticalNavigation from '@salesforce/design-system-react/components/vertical-navigation';
import Icon from '@salesforce/design-system-react/components/icon';

// Pages
import NPSPAccount from './pages/NPSPAccount';
import NPSPContact from './pages/NPSPContact';
import NPSPOpportunityMajor from './pages/NPSPOpportunityMajor';
import NPSPOpportunityStripe from './pages/NPSPOpportunityStripe';
import NPSPCampaign from './pages/NPSPCampaign';
import NPSPAIAssistant from './pages/NPSPAIAssistant';

const pages = [
  { id: 'overview', label: 'Overview', icon: 'home', component: null },
  { id: 'npsp-ai-assistant', label: 'AI Assistant', icon: 'einstein', component: NPSPAIAssistant },
  { id: 'npsp-account', label: 'Account (Organization)', icon: 'account', component: NPSPAccount },
  { id: 'npsp-contact', label: 'Contact (Donor)', icon: 'contact', component: NPSPContact },
  { id: 'npsp-opportunity-major', label: 'Opportunity (Major Gift)', icon: 'opportunity', component: NPSPOpportunityMajor },
  { id: 'npsp-opportunity-stripe', label: 'Opportunity (Stripe)', icon: 'opportunity', component: NPSPOpportunityStripe },
  { id: 'npsp-campaign', label: 'Campaign (Fundraising)', icon: 'campaign', component: NPSPCampaign },
];

const navCategories = [
  {
    id: 'npsp-nav',
    label: 'NPSP Pages',
    items: pages.map(p => ({ id: p.id, label: p.label })),
  },
];

function getPageDescription(id) {
  const descriptions = {
    'npsp-ai-assistant': 'AI-powered donor insights assistant for meeting preparation, donation history analysis, engagement recommendations, and contextual donor intelligence.',
    'npsp-account': 'Nonprofit organization record with giving history, engagement scoring, affiliated contacts, program enrollments, and activity timeline.',
    'npsp-contact': 'Donor profile with lifetime giving, recurring donations, wealth indicators, household relationships, and communication preferences.',
    'npsp-opportunity-major': 'Major gift donation ($1000+) with multi-installment payment tracking, GAU allocations, tribute information, acknowledgment workflow, and soft credit tracking.',
    'npsp-opportunity-stripe': 'Stripe-powered donation with recurring payment options (one-time, monthly, yearly), matching gifts, payment method management, and automated receipts.',
    'npsp-campaign': 'Fundraising campaign record with progress tracking, member management, campaign hierarchy, response rates, and milestone timeline.',
  };
  return descriptions[id] || '';
}

const iconColors = {
  'npsp-ai-assistant': '#9050e9',
  'npsp-account': '#7F8DE1',
  'npsp-contact': '#E27B7B',
  'npsp-opportunity': '#FCB95B',
  'npsp-campaign': '#F49756',
};

const OverviewPage = ({ onNavigate }) => (
  <div style={{ padding: '32px' }}>
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <h1 style={{ fontSize: '36px', fontWeight: '300', color: '#080707', marginBottom: '12px' }}>
        NPSP, Reimagined
      </h1>
      <p style={{ fontSize: '16px', color: '#706e6b', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6' }}>
        Explore reimagined Salesforce Nonprofit Success Pack interfaces — modern record pages for
        organizations, donors, donations, and fundraising campaigns built with Lightning Design System components.
      </p>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', maxWidth: '900px', margin: '0 auto' }}>
      {pages.filter(p => p.id !== 'overview').map(page => (
        <div
          key={page.id}
          onClick={() => onNavigate(page.id)}
          style={{
            backgroundColor: 'white',
            border: '1px solid #dddbda',
            borderRadius: '8px',
            padding: '24px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#0176d3'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(1,118,211,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#dddbda'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: iconColors[page.id] || '#0176d3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon category="standard" name={page.icon} size="small" style={{ fill: 'white' }} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>{page.label}</h3>
          </div>
          <p style={{ fontSize: '13px', color: '#706e6b', margin: 0, lineHeight: '1.5' }}>
            {getPageDescription(page.id)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const NPSPShowcaseHub = () => {
  const [activePage, setActivePage] = useState('overview');

  const ActiveComponent = pages.find(p => p.id === activePage)?.component;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{
        width: '260px',
        minWidth: '260px',
        backgroundColor: 'white',
        borderRight: '1px solid #dddbda',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #dddbda' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
            NPSP Showcase
          </h2>
          <p style={{ fontSize: '12px', color: '#706e6b', margin: '4px 0 0 0' }}>
            NPSP, Reimagined
          </p>
        </div>
        <VerticalNavigation
          categories={navCategories}
          selectedId={activePage}
          onSelect={(e, data) => setActivePage(data.item.id)}
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f3f3f3' }}>
        {activePage === 'overview'
          ? <OverviewPage onNavigate={setActivePage} />
          : ActiveComponent
            ? <ActiveComponent />
            : <div style={{ padding: '48px', textAlign: 'center', color: '#706e6b' }}>Page not found</div>
        }
      </div>
    </div>
  );
};

export default NPSPShowcaseHub;
